import React from 'react';
import { motion } from 'framer-motion';
import './Sectors.css';

interface Sector {
  id: number;
  icon: string;
  title: string;
  description: string;
  examples: string[];
}

const Sectors: React.FC = () => {
  const sectors: Sector[] = [
    {
      id: 1,
      icon: '🏪',
      title: 'Retail y Comercio',
      description:
        'Digitalizamos comercios físicos y online: TPV, e-commerce, gestión de stock e inventario y fidelización de clientes.',
      examples: ['E-commerce a medida', 'Apps de fidelización', 'TPV cloud', 'Gestión de stock'],
    },
    {
      id: 2,
      icon: '🍽️',
      title: 'Hostelería y Restauración',
      description:
        'Soluciones para restaurantes, bares, hoteles y apartamentos turísticos: reservas online, cartas digitales y gestión integral.',
      examples: ['Cartas QR', 'Reservas online', 'PMS para alojamientos', 'Pedidos a domicilio'],
    },
    {
      id: 3,
      icon: '🏥',
      title: 'Salud y Bienestar',
      description:
        'Plataformas para clínicas, consultas y centros de bienestar con cumplimiento RGPD y seguridad médica.',
      examples: ['Citas online', 'Historia clínica digital', 'Telemedicina', 'Apps de seguimiento'],
    },
    {
      id: 4,
      icon: '🏠',
      title: 'Inmobiliaria',
      description:
        'Portales inmobiliarios, CRMs, tours virtuales y automatización de captación y seguimiento de leads.',
      examples: ['Portales web', 'CRM inmobiliario', 'Tours 360º', 'Firma digital'],
    },
    {
      id: 5,
      icon: '⚖️',
      title: 'Legal y Asesorías',
      description:
        'Software para despachos, asesorías y gestorías: gestión documental, firma electrónica e IA legal.',
      examples: ['Gestión documental', 'Firma electrónica', 'IA legal asistida', 'Portales de cliente'],
    },
    {
      id: 6,
      icon: '🚐',
      title: 'Transporte y Movilidad',
      description:
        'Plataformas de movilidad compartida, logística, alquiler de vehículos y gestión de flotas.',
      examples: ['Marketplaces P2P', 'Tracking en tiempo real', 'Optimización de rutas', 'Gestión de flotas'],
    },
    {
      id: 7,
      icon: '🎓',
      title: 'Educación y Formación',
      description:
        'Plataformas e-learning, academias online, gestión de alumnos, facturación y campus virtuales.',
      examples: ['LMS a medida', 'Apps móviles educativas', 'Gestión académica', 'Examen online'],
    },
    {
      id: 8,
      icon: '🏭',
      title: 'Industria y B2B',
      description:
        'Digitalización industrial, IoT, dashboards de producción, integraciones con ERP y portales B2B.',
      examples: ['Dashboards en tiempo real', 'IoT industrial', 'Portales B2B', 'Integraciones ERP'],
    },
    {
      id: 9,
      icon: '🚀',
      title: 'Startups y MVPs',
      description:
        'Lanzamos productos digitales para startups: MVPs rápidos, validación de mercado y escalado iterativo.',
      examples: ['MVP en 4-8 semanas', 'Apps + Web compartidas', 'Stack escalable', 'Acompañamiento técnico'],
    },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="sectors" className="sectors section">
      <div className="container">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
        >
          <motion.h2 className="section-title neon-text animate-glow" variants={fadeInUp}>
            Sectores en los que Trabajamos
          </motion.h2>

          <motion.p className="sectors-subtitle" variants={fadeInUp}>
            Hemos digitalizado negocios de sectores muy distintos. Aplicamos las mismas reglas:
            seguridad desde el diseño, código limpio y soluciones que escalan con tu empresa.
          </motion.p>

          <motion.div className="sectors-grid" variants={staggerContainer}>
            {sectors.map((sector) => (
              <motion.div
                key={sector.id}
                className="sector-card glass-card"
                variants={fadeInUp}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="sector-icon">{sector.icon}</div>
                <h3 className="sector-title">{sector.title}</h3>
                <p className="sector-description">{sector.description}</p>
                <ul className="sector-examples">
                  {sector.examples.map((example, idx) => (
                    <li key={idx}>
                      <span className="example-bullet">▸</span>
                      {example}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>

          <motion.div className="sectors-cta" variants={fadeInUp}>
            <h3 className="gradient-text">¿Tu sector no está en la lista?</h3>
            <p>
              Tranquilo, hemos trabajado en sectores muy nicho y podemos adaptarnos al tuyo.
              Cuéntanos tu caso y te proponemos un plan de digitalización a medida.
            </p>
            <button className="btn-primary" onClick={scrollToContact}>
              Hablar con un especialista
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Sectors;
